import type { DependencyList, Dispatch, RefObject, SetStateAction } from 'react'

import React, { useCallback, useEffect, useRef, useState } from 'react'

import type { AnyFunction, CreateContextOptions, CreateContextReturn } from './types'

import { isArray, isFunction } from './assertion'
import { runIfFn } from './function'

type ReactRef<T> = React.Ref<T> | React.RefObject<T> | React.MutableRefObject<T>

/**
 * Assigns given value to the provided ref.
 * Supports ref as function (e.g. useState) and classic ref (e.g. useRef).
 */
export function assignRef<T = any>(ref: ReactRef<T> | undefined, value: T) {
  if (ref === null || ref === undefined) return

  if (isFunction(ref)) {
    ref(value)
    return
  }

  try {
    ref.current = value
  } catch (error) {
    throw new Error(`Cannot assign value '${value}' to ref '${ref}'`, { cause: error })
  }
}

/** Generic function to create new Context and context access hook. */
export function createContext<ContextType>(options: CreateContextOptions = {}) {
  const {
    errorMessage = 'Context can only be accessed by components wrapped with the Provider.',
    isOptional,
    name,
  } = options

  const Context = React.createContext<ContextType | undefined>(undefined)

  Context.displayName = name

  function useContext() {
    const context = React.useContext(Context)

    if (!isOptional && context === undefined) throw new Error(errorMessage)

    return context
  }

  return [Context.Provider, useContext] as CreateContextReturn<ContextType>
}

/** Assigns a node to all provided refs. */
export function mergeRefs<T>(...refs: (ReactRef<T> | undefined)[]) {
  return (node: T | null) => {
    refs.forEach(ref => assignRef(ref, node))
  }
}

// TODO: Bring it from UI-Utils
/** Returns given callback with a stable reference, but keeps it updated internally. */
export function useCallbackRef<T extends AnyFunction>(callback?: T, deps: DependencyList = []) {
  const innerRef = useRef(callback)

  innerRef.current = callback

  return useCallback(((...args) => innerRef.current?.(...args)) as T, deps)
}

const defaultEvents = ['mousedown', 'touchstart']

// TODO: Bring it from UI-Utils
/**
 * Triggers given callback when an event happens outside of a given element.
 * Event listeners are attached to the window object.
 * @param ref - element to check click position for
 * @param callback - function triggered on outside click
 * @param events - array of events listened for (default: ['mousedown', 'touchstart'])
 */
export function useClickOutside(
  ref: RefObject<HTMLElement | null> | RefObject<HTMLElement | null>[],
  callback?: (event: Event) => void,
  events: string[] = defaultEvents,
) {
  const eventsString = JSON.stringify(events)
  const listener = useCallbackRef((ev: Event) => {
    const refs = isArray(ref) ? ref : [ref]
    const target = ev.target as HTMLElement
    const isClickOutside = refs.every(ref => ref.current && !ref.current.contains(target))
    isClickOutside && callback?.(ev)
  })

  useEffect(() => {
    const events: string[] = JSON.parse(eventsString)
    events.forEach(event => window.addEventListener(event, listener))
    return () => {
      events.forEach(event => window.removeEventListener(event, listener))
    }
  }, [eventsString, ref])
}

export type UseControlledProps<T> = {
  defaultValue?: T
  onChange?: (value: T) => void
  value?: T
}

// TODO: Move to ui-utils
/** Supports state management in components that can be controlled or uncontrolled. */
export function useControlled<T>(props: UseControlledProps<T>) {
  const { defaultValue, onChange: onChangeProp, value: valueProp } = props
  const [valueState, setValueState] = useState(defaultValue)

  const isControlled = valueProp !== undefined
  const value = isControlled ? valueProp : valueState

  /** Triggers the external callback with the new value. In uncontrolled mode, updates state. */
  const onChange = useCallbackRef((next: SetStateAction<T>) => {
    const newValue = runIfFn(next)

    if (!isControlled) setValueState(newValue)

    onChangeProp?.(newValue)
  })

  /** In uncontrolled mode, sets the state to the default value. */
  const reset = useCallbackRef(() => {
    !isControlled && defaultValue && onChange(defaultValue)
  })

  return [value, onChange, { isControlled, reset }] as [
    T,
    Dispatch<SetStateAction<T>>,
    { isControlled: boolean; reset: () => void },
  ]
}

/** Returns an array with each prefix added to the id. Useful for aria attributes in compound components. */
export function useIds(id: string, prefixes: string[]) {
  return prefixes.map(prefix => `${prefix}-${id}`)
}

// TODO: Bring it from ui-utils
/**
 * Provides a function, which can be called to check if component using the hook is mounted or unmounted.
 */
export function useIsMounted() {
  const isMountedRef = useRef(false)

  useEffect(() => {
    isMountedRef.current = true

    return () => {
      isMountedRef.current = false
    }
  }, [])

  return useCallback(() => isMountedRef.current, [])
}
